import {
  stringLogicalOperators,
  numberLogicalOperators,
  dateStringLogicalOperators,
  selectionBasedLogicalOperators,
} from '@/composables/segmentations/logicalOperators';
import { fetchCustomSchemas } from '@/composables/custom-data/customData';
import { getProductCombinationsPropertiesSegments } from '@/composables/segmentations/themes/common-segments/productPropertiesSegment';

/**
 * Get product attributes segments (native properties only)
 */
const getProductAttributesSegments = async (includeContext = false) => {
  const productCombinations = await getProductCombinationsPropertiesSegments();

  // Native product properties only (custom properties are handled by getCustomAttributesSegments)
  const nativeProperties = [
    {
      id: 'name',
      label: 'segmentations.products.attributes.product_name',
      logical_operators: stringLogicalOperators('name'),
      ...(includeContext && { context: 'product.name' }),
    },
    {
      id: 'price',
      label: 'segmentations.products.attributes.product_price',
      logical_operators: numberLogicalOperators('price'),
      ...(includeContext && { context: 'product.price' }),
    },
    {
      id: 'price_discount',
      label: 'segmentations.products.attributes.product_price_discount',
      logical_operators: numberLogicalOperators('price_discount'),
      ...(includeContext && { context: 'product.price_discount' }),
    },
    {
      id: 'quantity',
      label: 'segmentations.products.attributes.product_quantity',
      logical_operators: numberLogicalOperators('quantity'),
      ...(includeContext && { context: 'product.quantity' }),
    },
    {
      id: 'products_combinations',
      label: 'segmentations.products.combinations.title',
      sub_properties: productCombinations,
    },
  ];

  return {
    id: 'products',
    label: 'segmentations.products.label',
    list: nativeProperties,
  };
};

/**
 * Get custom attributes segments for products
 */
const getCustomAttributesSegments = async (t: Function, includeContext = false) => {
  const customSchemas = await fetchCustomSchemas();

  // Filter schemas that have a relationship with products
  const productRelatedSchemas = customSchemas.filter((schema: any) => {
    if (!schema.relationships || !Array.isArray(schema.relationships)) {
      return false;
    }

    return schema.relationships.some(
      (rel: any) => rel.targetSchemaType === 'system' && rel.targetSchema === 'products',
    );
  });

  // Helper function to create field properties recursively
  const createFieldProperties = async (
    field: any,
    sourceSchema: any,
    allSchemas: any[],
    contextPath = '',
  ) => {
    // Check if this field has a relationship with another schema
    const relationship = sourceSchema.relationships?.find(
      (rel: any) => rel.sourceField === field.name
    );

    // If no relationship, create a simple field property
    if (!relationship) {
      // Determine logical operators based on field type
      let logicalOperators = {};

      switch (field.type) {
        case 'text':
          logicalOperators = stringLogicalOperators(`custom_${sourceSchema.id_custom_object}_${field.name}`);
          break;
        case 'number':
          logicalOperators = numberLogicalOperators(`custom_${sourceSchema.id_custom_object}_${field.name}`);
          break;
        case 'date':
          logicalOperators = dateStringLogicalOperators(`custom_${sourceSchema.id_custom_object}_${field.name}`);
          break;
        case 'boolean':
          logicalOperators = selectionBasedLogicalOperators(
            `custom_${sourceSchema.id_custom_object}_${field.name}`,
            [
              { label: t('common.yes'), value: true },
              { label: t('common.no'), value: false },
            ],
          );
          break;
        default:
          // Default to string operators if type is unknown
          logicalOperators = stringLogicalOperators(`custom_${sourceSchema.id_custom_object}_${field.name}`);
          break;
      }

      const property: any = {
        id: `custom_${sourceSchema.id_custom_object}_${field.name}`,
        label: field.label || field.name,
        logical_operators: logicalOperators,
        isCustomAttribute: true,
      };

      // Add context path if includeContext is true
      if (includeContext && contextPath) {
        property.context = `${contextPath}.${field.name}`;
      }

      return property;
    }

    // Handle system schema relationships
    if (relationship.targetSchemaType === 'system') {
      // For system relationships (like orders, customers, etc.), create a simple field for now
      return {
        id: `custom_${sourceSchema.id_custom_object}_${field.name}`,
        label: field.label || field.name,
        logical_operators: stringLogicalOperators(`custom_${sourceSchema.id_custom_object}_${field.name}`),
        isCustomAttribute: true,
      };
    }

    // Handle custom schema relationships
    if (relationship.targetSchemaType === 'custom') {
      // Find the target schema
      const targetSchema = allSchemas.find(
        (schema) => schema.id_custom_object.toString() === relationship.targetSchema.toString(),
      );

      // If target schema not found, return simple field
      if (!targetSchema) {
        return {
          id: `custom_${sourceSchema.id_custom_object}_${field.name}`,
          label: field.label || field.name,
          logical_operators: stringLogicalOperators(`custom_${sourceSchema.id_custom_object}_${field.name}`),
          isCustomAttribute: true,
        };
      }

      // Create sub-properties for the target schema fields
      // EXTEND the contextPath with the target schema table_name for nested joins
      // Example: product.custom.schema1 -> product.custom.schema1.schema2
      const extendedContextPath = contextPath && includeContext
        ? `${contextPath}.${targetSchema.table_name}`
        : contextPath;
      const subProperties = await Promise.all(
        targetSchema.fields.map((targetField: any) => createFieldProperties(targetField, targetSchema, allSchemas, extendedContextPath)),
      );

      // Return a complex segment
      return {
        id: `custom_${sourceSchema.id_custom_object}_${field.name}`,
        label: `${field.name} (${targetSchema.schema_name})`,
        sub_properties: subProperties,
        isCustomAttribute: true,
      };
    }

    // Default fallback
    return {
      id: `custom_${sourceSchema.id_custom_object}_${field.name}`,
      label: field.label || field.name,
      logical_operators: stringLogicalOperators(`custom_${sourceSchema.id_custom_object}_${field.name}`),
      isCustomAttribute: true,
    };
  };

  // Create segments for each schema
  const segmentsList = await Promise.all(
    productRelatedSchemas.map(async (schema: any) => {
      if (!schema.relationships || !Array.isArray(schema.relationships)) {
        return [];
      }
      // Find the field name used for product relationship to exclude it
      const productRelationFields = schema.relationships
        .filter((rel: any) => rel.targetSchemaType === 'system' && rel.targetSchema === 'products')
        .map((rel: any) => rel.sourceField);

      // Create sub-properties for each field in the schema, excluding product relation fields
      // Initialize contextPath with product.custom.{table_name}
      const initialContextPath = includeContext ? `product.custom.${schema.table_name}` : '';
      const subProperties = await Promise.all(
        schema.fields
          .filter((field: any) => !productRelationFields.includes(field.name))
          .map((field: any) => createFieldProperties(field, schema, customSchemas, initialContextPath)),
      );

      // Return segment for this schema
      return {
        id: `custom_${schema.id_custom_object}`,
        label: schema.schema_name,
        sub_properties: subProperties,
        isCustomAttribute: true,
      };
    }),
  );

  return {
    id: 'productCustomAttributes',
    label: 'segmentations.customAttributes.label',
    list: segmentsList,
  };
};

/**
 * Get theme products definition
 */
const getThemeProductsDefinition = async (t: Function, includeContext = false) => {
  try {
    const [productAttributes, customAttributes] = await Promise.all([
      getProductAttributesSegments(includeContext),
      getCustomAttributesSegments(t, includeContext),
    ]);

    const groupProperties: any = [productAttributes];
    if (customAttributes.list.length > 0) {
      groupProperties.push(customAttributes);
    }

    return groupProperties;
  } catch (error) {
    console.error('An error occurred while fetching product segments:', error);
  }
  return [];
};

export {
  getProductAttributesSegments,
  getCustomAttributesSegments,
  getThemeProductsDefinition,
};
